import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Download, Eye, Pencil, RotateCcw, Search, Stethoscope, X } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import { INTEGRATION_DOCTOR_SHARE_DETAIL, PAYROLL_DOCTOR_SHARE_ADJUSTMENT } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";

const PAGE_SIZES = [25, 50, 100];

const inr = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;

const Spinner = () => (
    <div className="py-12 flex justify-center">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-500" />
    </div>
);

const Empty = ({ children }) => (
    <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{children}</p>
);

const ModuleBadge = ({ value }) => (
    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
        {value || "—"}
    </span>
);

const EditedBadge = ({ title }) => (
    <span title={title} className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        Edited
    </span>
);

const Th = ({ children, right, center }) => (
    <th className={`py-2 px-3 font-medium whitespace-nowrap ${right ? "text-right" : center ? "text-center" : "text-left"}`}>{children}</th>
);

const Card = ({ label, value, tone = "gray" }) => {
    const tones = {
        gray: "border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-gray-100",
        purple: "border-purple-100 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300",
        amber: "border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300",
    };
    return (
        <div className={`rounded-xl border px-4 py-3 ${tones[tone]}`}>
            <div className="text-xs opacity-80">{label}</div>
            <div className="text-lg font-semibold">{inr(value)}</div>
        </div>
    );
};

const editTitle = (r) =>
    r.is_edited
        ? `eMedHub share at edit: ${inr(r.original_share_at_edit)}\nReason: ${r.reason || "—"}\nBy: ${r.edited_by || "—"}${r.edited_at ? ` on ${new Date(r.edited_at).toLocaleString("en-IN")}` : ""}`
        : undefined;

/**
 * eMedHub doctor-share breakdown for one employee over a payroll period.
 * Mirrors eMedHub's "Doctor Wise - Service Consolidate Report": a summary per
 * module + service head, and an eye button that opens the invoices behind a row.
 * While the payslip is unpublished, each invoice row's share can be edited; the
 * edited (final) share is what payroll pays.
 */
const DoctorShareDetailModal = ({ employee, fromDate, toDate, editable, onClose, onSaved }) => {
    const { fetch, update } = useApiMethods();
    const [exporting, setExporting] = useState(false);

    // Full breakdown (every service head, every invoice, eMedHub and final share)
    // as Excel. Can take a while: the server fetches each service head from eMedHub.
    const exportExcel = async () => {
        setExporting(true);
        try {
            const blob = await apiClient.get(`/${INTEGRATION_DOCTOR_SHARE_DETAIL}`, {
                params: { employee_id: employee.employee, from_date: fromDate, to_date: toDate, export_type: "xlsx" },
                responseType: "blob",
                timeout: 120000,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Doctor_Share_${(employee?.employee_name || "Employee").trim().replace(/\s+/g, "_")}_${fromDate}_to_${toDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            // 401/403/network errors are already toasted by apiClient.
            const status = error?.response?.status;
            if (status && status !== 401 && status !== 403) {
                let message = "Could not export doctor share";
                try {
                    message = JSON.parse(await error.response.data.text())?.message || message;
                } catch (_) { /* not JSON */ }
                ToastMsg("error", message);
            }
        } finally {
            setExporting(false);
        }
    };

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // The summary row being drilled into; null while on the overview.
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [editedOnly, setEditedOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    // Unsaved edits on the open service head: { [line_key]: "250.00" }.
    const [drafts, setDrafts] = useState({});
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const baseParams = { employee_id: employee.employee, from_date: fromDate, to_date: toDate };

    const loadSummary = async () => {
        const res = await fetch(INTEGRATION_DOCTOR_SHARE_DETAIL, baseParams, setSummaryLoading);
        if (res?.status === "success") {
            setSummary(res.data || { data: [], Total_amount_card: "0.00", Doctor_share_card: "0.00", Final_share_card: "0.00" });
            return true;
        }
        ToastMsg("error", res?.message || "Could not load doctor share details");
        return false;
    };

    const loadDetail = async (row) => {
        const res = await fetch(INTEGRATION_DOCTOR_SHARE_DETAIL, {
            ...baseParams,
            view_type: "detailed",
            module: row.module_type,
            service_head: row.service_head,
        }, setDetailLoading);
        if (res?.status === "success") {
            setDetail(res.data || { data: [] });
            return true;
        }
        ToastMsg("error", res?.message || "Could not load invoice details");
        return false;
    };

    useEffect(() => {
        loadSummary().then((ok) => !ok && onClose());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee, fromDate, toDate]);

    const rows = useMemo(() => detail?.data || [], [detail]);
    const rowsByKey = useMemo(() => Object.fromEntries(rows.map((r) => [r.line_key, r])), [rows]);

    // Drafts that actually change something, with their validation state.
    const changes = useMemo(() => Object.entries(drafts).map(([key, value]) => {
        const row = rowsByKey[key];
        if (!row) return null;
        const num = Number(value);
        const valid = value !== "" && Number.isFinite(num) && num >= 0 && num <= Number(row.total_amount || 0);
        return { key, row, value, num, valid, changed: !valid || round2(num) !== round2(row.final_share) };
    }).filter((c) => c && c.changed), [drafts, rowsByKey]);

    const isDirty = changes.length > 0;
    const invalidCount = changes.filter((c) => !c.valid).length;
    const pendingDelta = changes.filter((c) => c.valid).reduce((sum, c) => sum + (c.num - Number(c.row.final_share || 0)), 0);
    // A reason is needed unless every change just puts a row back to eMedHub's share.
    const needsReason = changes.some((c) => c.valid && round2(c.num) !== round2(c.row.doctor_share));

    const confirmDiscard = useCallback(
        () => !isDirty || window.confirm("You have unsaved doctor share changes. Discard them?"),
        [isDirty]
    );

    const requestClose = useCallback(() => {
        if (confirmDiscard()) onClose();
    }, [confirmDiscard, onClose]);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && requestClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [requestClose]);

    const resetDetailState = () => {
        setDrafts({});
        setReason("");
        setSearch("");
        setEditedOnly(false);
        setPage(1);
    };

    const openDetail = async (row) => {
        setSelected(row);
        setDetail(null);
        resetDetailState();
        if (!(await loadDetail(row))) setSelected(null);
    };

    const backToOverview = () => {
        if (!confirmDiscard()) return;
        setSelected(null);
        resetDetailState();
    };

    const startEdit = (r) => setDrafts((d) => ({ ...d, [r.line_key]: Number(r.final_share || 0).toFixed(2) }));
    const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));
    const dropDraft = (key) => setDrafts(({ [key]: _, ...rest }) => rest);
    // Undo a saved edit: queue the row back at eMedHub's share (saving removes the edit).
    const revertToOriginal = (r) => setDraft(r.line_key, Number(r.doctor_share || 0).toFixed(2));

    const save = async () => {
        if (!isDirty || invalidCount || (needsReason && !reason.trim())) return;
        const res = await update(PAYROLL_DOCTOR_SHARE_ADJUSTMENT, {
            payslip_id: employee.payslip_id,
            module: selected.module_type,
            service_head: selected.service_head,
            changes: changes.map((c) => ({ line_key: c.key, final_share: round2(c.num), reason: reason.trim() })),
        }, false, setSaving);
        if (res?.status !== "success") {
            ToastMsg("error", res?.message || "Could not save doctor share changes");
            return;
        }
        ToastMsg("success", res.message || "Doctor share updated");
        setDrafts({});
        setReason("");
        await Promise.all([loadDetail(selected), loadSummary()]);
        onSaved?.(res.data);
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (editedOnly && !r.is_edited && !(r.line_key in drafts)) return false;
            if (!q) return true;
            return [r.patient_name, r.patient_account_number, r.invoice_number, r.invoice_date, r.service_name]
                .some((v) => String(v || "").toLowerCase().includes(q));
        });
    }, [rows, search, editedOnly, drafts]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const currentPage = Math.min(page, pageCount);
    const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const rangeStart = filtered.length ? (currentPage - 1) * pageSize + 1 : 0;
    const rangeEnd = Math.min(currentPage * pageSize, filtered.length);

    const selectedSummary = selected && summary
        ? (summary.data || []).find((r) => r.module_type === selected.module_type && r.service_head === selected.service_head) || selected
        : selected;

    const StaleWarning = ({ count }) => count ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            {count} edited row{count > 1 ? "s are" : " is"} no longer in eMedHub (bill cancelled or changed) and {count > 1 ? "are" : "is"} not being applied.
        </div>
    ) : null;

    const renderSummary = () => {
        if (summaryLoading || !summary) return <Spinner />;
        const list = summary.data || [];
        const hasEdits = round2(summary.Final_share_card) !== round2(summary.Doctor_share_card);
        return (
            <>
                <StaleWarning count={summary.stale_edits} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Card label="Total Amount" value={summary.Total_amount_card} />
                    <Card label="eMedHub Doctor Share" value={summary.Doctor_share_card} />
                    <Card label="Final Doctor Share (paid)" value={summary.Final_share_card ?? summary.Doctor_share_card} tone={hasEdits ? "amber" : "purple"} />
                </div>

                {!list.length ? (
                    <Empty>No sharable services found for this period.</Empty>
                ) : (
                    <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/70 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-700">
                                    <Th>Module</Th>
                                    <Th>Service Head</Th>
                                    <Th right>Count</Th>
                                    <Th right>Total Amount</Th>
                                    <Th right>Doctor Share</Th>
                                    <Th right>Final Share</Th>
                                    <Th center>Action</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((r, i) => {
                                    const edited = r.edited_count > 0;
                                    return (
                                        <tr key={`${r.module_type}-${r.service_head}-${i}`} className={`border-b border-gray-50 dark:border-gray-800 last:border-0 ${edited ? "bg-amber-50/50 dark:bg-amber-900/10" : ""}`}>
                                            <td className="py-2 px-3"><ModuleBadge value={r.module_type} /></td>
                                            <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                                                {r.service_head || "—"}
                                                {edited && <EditedBadge title={`${r.edited_count} invoice row(s) edited`} />}
                                            </td>
                                            <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{r.count}</td>
                                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{inr(r.total_amount)}</td>
                                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{inr(r.doctor_share)}</td>
                                            <td className={`py-2 px-3 text-right font-semibold ${edited ? "text-amber-700 dark:text-amber-300" : "text-purple-600 dark:text-purple-400"}`}>
                                                {inr(r.final_share ?? r.doctor_share)}
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                                <button
                                                    type="button"
                                                    title={editable ? "View and edit invoices" : "View invoices"}
                                                    onClick={() => openDetail(r)}
                                                    className="p-1.5 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    const renderFinalCell = (r) => {
        if (r.line_key in drafts) {
            const value = drafts[r.line_key];
            const num = Number(value);
            const invalid = value === "" || !Number.isFinite(num) || num < 0 || num > Number(r.total_amount || 0);
            return (
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={r.total_amount}
                    autoFocus
                    value={value}
                    onChange={(e) => setDraft(r.line_key, e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), dropDraft(r.line_key))}
                    title={invalid ? `Must be between 0 and ${inr(r.total_amount)}` : undefined}
                    className={`w-28 rounded-md border px-2 py-1 text-right text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 ${invalid ? "border-red-400 focus:ring-red-200" : "border-purple-300 focus:ring-purple-200"}`}
                />
            );
        }
        return (
            <span className={`font-semibold ${r.is_edited ? "text-amber-700 dark:text-amber-300" : "text-purple-600 dark:text-purple-400"}`} title={editTitle(r)}>
                {inr(r.final_share)}
            </span>
        );
    };

    const renderDetail = () => (
        <>
            <button
                type="button"
                onClick={backToOverview}
                className="inline-flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300 hover:underline"
            >
                <ArrowLeft size={16} /> Back to overview
            </button>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm">
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Service Head</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <ModuleBadge value={selectedSummary.module_type} /> {selectedSummary.service_head}
                    </div>
                </div>
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Service Count</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{selectedSummary.count}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Amount</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{inr(selectedSummary.total_amount)}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Doctor Share</div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{inr(selectedSummary.doctor_share)}</div>
                </div>
                <div>
                    <div className="text-xs text-purple-700 dark:text-purple-300">Final Share</div>
                    <div className="font-semibold text-purple-700 dark:text-purple-300">{inr(detail?.Final_share_card ?? selectedSummary.final_share)}</div>
                </div>
            </div>

            <div className="mt-4"><StaleWarning count={detail?.stale_edits} /></div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={editedOnly}
                        onChange={(e) => { setEditedOnly(e.target.checked); setPage(1); }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-300"
                    />
                    Edited only
                </label>
                <div className="relative w-full sm:w-72">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Search patient, UHID, invoice"
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                </div>
            </div>

            {detailLoading || !detail ? (
                <Spinner />
            ) : !filtered.length ? (
                <Empty>{search || editedOnly ? "No invoices match these filters." : "No invoices found for this service."}</Empty>
            ) : (
                <>
                    <div className="mt-3 overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/70 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-700">
                                    <Th>Patient</Th>
                                    <Th>Invoice Number</Th>
                                    <Th>Invoice Date</Th>
                                    <Th>Service Name</Th>
                                    <Th right>Count</Th>
                                    <Th right>Total Amount</Th>
                                    <Th right>Doctor Share</Th>
                                    <Th right>Final Share</Th>
                                    {editable && <Th center>Action</Th>}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((r) => {
                                    const drafting = r.line_key in drafts;
                                    return (
                                        <tr key={r.line_key} className={`border-b border-gray-50 dark:border-gray-800 last:border-0 ${drafting ? "bg-purple-50/60 dark:bg-purple-900/10" : r.is_edited ? "bg-amber-50/50 dark:bg-amber-900/10" : ""}`}>
                                            <td className="py-2 px-3">
                                                <div className="font-medium text-gray-900 dark:text-gray-100">{r.patient_name || "—"}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{r.patient_account_number}</div>
                                            </td>
                                            <td className="py-2 px-3 text-gray-700 dark:text-gray-200 whitespace-nowrap">{r.invoice_number || "—"}</td>
                                            <td className="py-2 px-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{r.invoice_date || "—"}</td>
                                            <td className="py-2 px-3 text-gray-700 dark:text-gray-200">{r.service_name || "—"}</td>
                                            <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{r.count}</td>
                                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{inr(r.total_amount)}</td>
                                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{inr(r.doctor_share)}</td>
                                            <td className="py-2 px-3 text-right whitespace-nowrap">
                                                {renderFinalCell(r)}
                                                {r.is_edited && !drafting && <EditedBadge title={editTitle(r)} />}
                                            </td>
                                            {editable && (
                                                <td className="py-2 px-3 text-center whitespace-nowrap">
                                                    {drafting ? (
                                                        <button type="button" title="Cancel this change" onClick={() => dropDraft(r.line_key)}
                                                            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button type="button" title="Edit final share" onClick={() => startEdit(r)}
                                                                className="p-1.5 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400">
                                                                <Pencil className="h-4 w-4" />
                                                            </button>
                                                            {r.is_edited && (
                                                                <button type="button" title="Undo edit (back to eMedHub share)" onClick={() => revertToOriginal(r)}
                                                                    className="p-1.5 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                                                                    <RotateCcw className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-end gap-3 text-sm text-gray-500 dark:text-gray-400">
                        <label className="inline-flex items-center gap-2">
                            Rows per page
                            <select
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                            >
                                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <span>{rangeStart}–{rangeEnd} of {filtered.length}</span>
                        <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}
                            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent">
                            <ChevronLeft size={18} />
                        </button>
                        <button type="button" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}
                            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent">
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </>
            )}
        </>
    );

    const renderSaveBar = () => (
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-5 py-3">
            <div className="text-sm text-gray-700 dark:text-gray-200">
                <span className="font-semibold">{changes.length}</span> row{changes.length > 1 ? "s" : ""} changed
                {invalidCount > 0 ? (
                    <span className="ml-2 text-red-600 dark:text-red-400">· {invalidCount} invalid</span>
                ) : (
                    <span className={`ml-2 font-medium ${pendingDelta < 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                        · {pendingDelta < 0 ? "−" : "+"}{inr(Math.abs(pendingDelta))}
                    </span>
                )}
            </div>
            <input
                value={reason}
                maxLength={255}
                onChange={(e) => setReason(e.target.value)}
                placeholder={needsReason ? "Reason for these changes (required)" : "Reason (optional when undoing)"}
                className="flex-1 min-w-[220px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
            <button type="button" onClick={() => { setDrafts({}); setReason(""); }} disabled={saving}
                className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                Discard
            </button>
            <button type="button" onClick={save} disabled={saving || invalidCount > 0 || (needsReason && !reason.trim())}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:hover:bg-purple-600">
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={requestClose}>
            <div
                className="w-full max-w-full max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 grid place-items-center">
                            <Stethoscope size={20} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">eMedHub Doctor Share</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {employee?.employee_name || "Employee"}
                                {fromDate ? ` · ${fromDate} to ${toDate}` : ""}
                                {!editable && " · read-only (published)"}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={exportExcel}
                            disabled={exporting || !summary}
                            title="Download every invoice with eMedHub and final share"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50"
                        >
                            <Download size={16} /> {exporting ? "Exporting..." : "Export Excel"}
                        </button>
                        <button onClick={requestClose} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="p-5 overflow-auto flex-1">{selected ? renderDetail() : renderSummary()}</div>

                {selected && editable && isDirty && renderSaveBar()}
            </div>
        </div>
    );
};

export default DoctorShareDetailModal;
